import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { NotificationSettingsPanel } from "@/components/settings/NotificationSettings";
import { GeneralSettings } from "@/components/settings/GeneralSettings";
import NotificationRouting from "@/components/settings/NotificationRouting";
import { PlanSettings } from "@/components/settings/PlanSettings";
import { WhitelabelSettings } from "@/components/settings/WhitelabelSettings";
import { MCPSettings } from "@/components/settings/MCPSettings";
import { LearningConfig } from "@/components/settings/LearningConfig";
import { AutonomyDashboard } from "@/components/settings/AutonomyDashboard";
import { Terminal, Settings as SettingsIcon, Shield, CreditCard, Activity, Crown, Bot, Brain, ShieldAlert } from "lucide-react";

type Tab = "general" | "notifications" | "plan" | "brand" | "mcp" | "learning" | "autonomy";

import { useSearchParams } from "react-router-dom";

export default function Settings() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = (searchParams.get("tab") as Tab) || "general";
  const [tab, setTab] = useState<Tab>(initialTab);

  const tabs: { id: Tab; label: string; icon: any }[] = [
    { id: "general", label: "General", icon: SettingsIcon },
    { id: "notifications", label: "Alerts", icon: Shield },
    { id: "plan", label: "Billing", icon: CreditCard },
    { id: "brand", label: "Brand", icon: Crown },
    { id: "mcp", label: "GPT Settings", icon: Bot },
    { id: "learning", label: "AI Learning", icon: Brain },
    { id: "autonomy", label: "Autonomy", icon: ShieldAlert },
  ];

  const handleTabChange = (id: Tab) => {
    setTab(id);
    setSearchParams({ tab: id });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="max-w-6xl mx-auto flex flex-col gap-6 pb-6 lg:h-[calc(100vh-140px)] lg:overflow-hidden"
    >
      <header className="space-y-4 shrink-0">
        <div className="flex items-center gap-3">
          <Terminal className="h-6 w-6 text-primary" />
          <h1 className="text-4xl font-black uppercase tracking-tighter italic">Terminal Config</h1>
        </div>
        <p className="max-w-2xl text-[10px] font-bold text-muted-foreground uppercase tracking-[0.2em] leading-relaxed">
          Manage your telemetry resolution, notification channels, and operational profile. 
        </p>
        <div className="h-1.5 w-full bg-foreground" />
      </header>
 
      <div className="grid grid-cols-1 lg:grid-cols-[250px_1fr] gap-12 items-start lg:flex-1 lg:min-h-0 lg:overflow-hidden">
        {/* Sidebar Tabs */}
        <aside className="space-y-2 shrink-0">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => handleTabChange(t.id)}
              className={`w-full flex items-center gap-3 px-4 py-4 text-[10px] font-black uppercase tracking-widest transition-all border-4 ${
                tab === t.id
                  ? "bg-foreground text-background border-foreground shadow-[4px_4px_0_0_#ef4444]"
                  : "bg-background text-muted-foreground border-transparent hover:border-foreground/20 hover:text-foreground"
              }`}
            >
              <t.icon className={`h-4 w-4 ${tab === t.id ? "text-primary" : ""}`} />
              {t.label}
            </button>
          ))}
        </aside>
 
        {/* Content Area */}
        <div className="bg-background border-4 border-foreground p-8 shadow-brutal min-h-[500px] lg:h-full lg:overflow-y-auto custom-scrollbar">
          <AnimatePresence mode="wait">
            <motion.div
              key={tab}
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              transition={{ duration: 0.2 }}
            >
              {tab === "general" && <GeneralSettings />}
              {tab === "notifications" && (
                <div className="space-y-12">
                  <NotificationSettingsPanel />
                  <NotificationRouting />
                </div>
              )}
              {tab === "plan" && <PlanSettings />}
              {tab === "brand" && <WhitelabelSettings />}
              {tab === "mcp" && <MCPSettings />}
              {tab === "learning" && <LearningConfig />}
              {tab === "autonomy" && <AutonomyDashboard />}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
}
